import { AxiosError } from 'axios';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import { alertDialog } from '@/ui_v3';

import { classifyWebsiteError } from '../errorClassifier';
import { getWebsiteAlertMessages } from '../errorMessages';
import type { AlertGroup, ValidationResult } from '../types';

type Stage = 'preview' | 'generate';

const EVENT_BY_STAGE: Record<Stage, string> = {
  preview: 'ai_website_metadata_error',
  generate: 'ai_website_generate_error',
};

/**
 * Website AI 에러 알럿 통합 훅.
 *
 * 알럿 다이얼로그 + 로깅의 단일 사이드 이펙트 지점이다. 두 종류의 진입 경로를
 * 노출한다:
 *
 *  - `from*`: 원시 입력(검증 실패, API 에러)을 받아 classifier로 group/reason을
 *    도출한 뒤 render에 위임.
 *  - `for*`: 호출자가 이미 도메인 분류를 마친 _알려진 시그널_에 대해 메시지·
 *    로깅 단계를 통일하기 위해 render에 직접 위임. classifier를 거치지 않는다.
 *
 * 로깅 이벤트명은 backwards-compat을 위해 단계별로 유지하되 payload에
 * `alert_group`과 `http_status`를 새로 추가한다 — 기존 `reason` 차원과
 * 신규 그룹 차원으로 동시에 분석 가능.
 */
export const useWebsiteErrorAlert = (stage: Stage) => {
  const { t } = useTranslation(['build']);
  const messages = getWebsiteAlertMessages(t);
  const eventName = EVENT_BY_STAGE[stage];

  const render = (group: AlertGroup, reason: string, httpStatus?: number) => {
    const message = messages[group];
    alertDialog.open({
      header: { title: message.title },
      ...(message.body ? { body: message.body } : {}),
    });
    logger.track(eventName, {
      reason,
      alert_group: group,
      http_status: httpStatus,
    });
  };

  return {
    /** 서버 에러 → classifier가 group을 도출한 뒤 render. */
    fromApiError: (error: unknown) => {
      const { group, reason } = classifyWebsiteError({
        kind: 'apiError',
        error,
        stage,
      });
      const httpStatus =
        error instanceof AxiosError ? error.response?.status : undefined;
      render(group, reason, httpStatus);
    },

    /** 클라이언트 검증 실패 → classifier가 group을 도출한 뒤 render. */
    fromValidation: (result: Extract<ValidationResult, { isValid: false }>) => {
      const { group, reason } = classifyWebsiteError({
        kind: 'validation',
        result,
      });
      render(group, reason);
    },

    /**
     * preview API가 200으로 응답했지만 본문에서 사용 불가가 드러난 케이스
     * (`isExtractable === false`). 분류 대상이 아닌 _알려진 도메인 시그널_이라
     * classifier를 거치지 않고 메시지·로깅 단계 통일을 위해서만 render에 위임.
     */
    forUnusableContent: () => {
      render('unusableWebsite', 'is_extractable_false');
    },
  };
};
